// ─── Shared GroupCard for sync conflict views ───────────────────────────────
//
// Renders a labelled card that groups field comparisons under a heading with
// semantic <table>/<thead>/<th> for the "Client | Server" column headers.
//
// Mobile responsive: on screens < md, shows stacked layout with labeled values.
// Desktop: standard 3-column table (Field | Client | Server).

import type { FieldSpec } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { GLASS_FORM, cn } from '../../../constants/design-tokens';
import { FieldRow } from './FieldRow';

export interface GroupCardProps {
  title: string;
  fields: FieldSpec[];
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  delay: number;
  /** Tenant currency for `currency`-format fields. Defaults to `'INR'`. */
  currency?: CurrencyCode;
}

export function GroupCard({
  title,
  fields,
  clientVersion,
  serverVersion,
  delay,
  currency = 'INR',
}: GroupCardProps) {
  return (
    <section
      className={cn(GLASS_FORM, 'p-4 animate-slide-up')}
      style={{ animationDelay: `${delay}ms` }}
      role="region"
      aria-label={`${title} comparison`}
    >
      {/* Desktop: table layout */}
      <table className="w-full border-collapse hidden md:table" role="table">
        <thead>
          <tr>
            <th
              scope="col"
              className="text-left px-3 py-1 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 w-[140px]"
            >
              {title}
            </th>
            <th
              scope="col"
              className="text-left px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-medium"
            >
              Client
            </th>
            <th
              scope="col"
              className="text-left px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-medium"
            >
              Server
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((spec) => (
            <FieldRow
              key={spec.key}
              spec={spec}
              clientVersion={clientVersion}
              serverVersion={serverVersion}
              currency={currency}
            />
          ))}
        </tbody>
      </table>

      {/* Mobile: stacked layout */}
      <div className="md:hidden">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 px-1 pb-2">
          {title}
        </h4>
        <div className="space-y-2">
          {fields.map((spec) => (
            <FieldRow
              key={spec.key}
              spec={spec}
              clientVersion={clientVersion}
              serverVersion={serverVersion}
              currency={currency}
              stacked
            />
          ))}
        </div>
      </div>
    </section>
  );
}
